/**
 * Battle screen controller.
 * Shows the battle state from the player's perspective, handles action selection,
 * turn timer, chat, and forfeit/rematch.
 * Receives battle state updates from the engine via the sync manager.
 */

import type { ScreenId } from "./home";
import type { BattleState, BattleAction, PlayerId, PokemonBattleState, BattleConfig } from "../../engine/types";
import { el, clearChildren } from "../utils/dom";
import { button, toast } from "../components";
import { pokemonBackUrl, pokemonFrontUrl, pokemonMenuUrl, backgroundImageUrl } from "../../data/images";
import { resolveSpriteUrl, preloadSprites } from "../../data/sprites";
import type { BattleSession } from "../battle-session";
import { clearBattleSession, resetBattleSession, generateSeed } from "../battle-session";
import { audioManager } from "../../data/audio";
import { REDUCED_MOTION_QUERY } from "../../config/constants";

export function renderBattle(
  container: HTMLElement,
  navigate: (screen: ScreenId) => void,
  session: BattleSession,
): void {
  clearChildren(container);

  const { engine, sync, recorder, mySide, config, myProfile, opponentProfile } = session;
  const foeSide: PlayerId = mySide === "p1" ? "p2" : "p1";
  const prefersReducedMotion = window.matchMedia(REDUCED_MOTION_QUERY).matches;

  const root = el("div", { className: "battle-root" });

  // Background
  const bg = el("div", { className: "battle-bg" });
  void resolveSpriteUrl(backgroundImageUrl(config.battleBackground)).then((url) => {
    if (url) bg.style.backgroundImage = `url(${url})`;
  });
  root.appendChild(bg);

  // Opponent area (top)
  const foeArea = el("div", { className: "battle-area battle-foe-area" });
  const foeInfo = el("div", { className: "battle-trainer-info" });
  foeInfo.appendChild(el("span", { text: opponentProfile.name, className: "trainer-name" }));
  foeArea.appendChild(foeInfo);
  const foeSprite = el("img", { className: "battle-sprite battle-foe-sprite sprite-animated", attrs: { alt: "Opponent Pokemon" } }) as HTMLImageElement;
  foeSprite.style.imageRendering = "pixelated";
  foeArea.appendChild(foeSprite);
  const foeHpBar = createHpBar();
  foeArea.appendChild(foeHpBar.container);
  root.appendChild(foeArea);

  // Player area (bottom)
  const myArea = el("div", { className: "battle-area battle-my-area" });
  const myInfo = el("div", { className: "battle-trainer-info" });
  myInfo.appendChild(el("span", { text: myProfile.name, className: "trainer-name" }));
  myArea.appendChild(myInfo);
  const mySprite = el("img", { className: "battle-sprite battle-my-sprite sprite-animated", attrs: { alt: "My Pokemon" } }) as HTMLImageElement;
  mySprite.style.imageRendering = "pixelated";
  myArea.appendChild(mySprite);
  const myHpBar = createHpBar();
  myArea.appendChild(myHpBar.container);
  root.appendChild(myArea);

  // Turn info
  const turnInfo = el("div", { className: "battle-turn-info" });
  root.appendChild(turnInfo);

  // Action panel
  const actionPanel = el("div", { className: "battle-action-panel" });
  root.appendChild(actionPanel);

  // Chat panel wrapper: desktop inline panel + mobile drawer
  const chatLog = el("div", { className: "battle-chat-log" });
  const chatInput = el("input", { className: "battle-chat-input", attrs: { type: "text", placeholder: "Type a message..." } }) as HTMLInputElement;
  const chatSend = button("Send", () => {
    const text = chatInput.value.trim();
    if (text) {
      void sync.sendChat(text);
      chatInput.value = "";
      addChatMessage(chatLog, myProfile.name, text);
    }
  }, { variant: "secondary" });

  const chatPanel = el("div", { className: "battle-chat-panel" });
  chatPanel.appendChild(chatLog);
  chatPanel.appendChild(chatInput);
  chatPanel.appendChild(chatSend);
  root.appendChild(chatPanel);

  // Mobile chat drawer and backdrop
  const chatDrawer = el("div", { className: "chat-drawer" });
  const drawerHeader = el("div", { className: "chat-drawer-header" });
  drawerHeader.appendChild(el("span", { text: "Chat", className: "chat-drawer-title" }));
  const closeDrawerBtn = el("button", { className: "chat-drawer-close", text: "×" });
  drawerHeader.appendChild(closeDrawerBtn);
  const drawerLog = el("div", { className: "battle-chat-log" });
  const drawerInput = el("input", { className: "battle-chat-input", attrs: { type: "text", placeholder: "Type a message..." } }) as HTMLInputElement;
  const drawerSend = button("Send", () => {
    const text = drawerInput.value.trim();
    if (text) {
      void sync.sendChat(text);
      drawerInput.value = "";
      addChatMessage(drawerLog, myProfile.name, text);
      addChatMessage(chatLog, myProfile.name, text); // keep desktop log in sync if visible
    }
  }, { variant: "secondary" });
  chatDrawer.appendChild(drawerHeader);
  chatDrawer.appendChild(drawerLog);
  chatDrawer.appendChild(drawerInput);
  chatDrawer.appendChild(drawerSend);
  const chatBackdrop = el("div", { className: "chat-drawer-backdrop" });
  root.appendChild(chatBackdrop);
  root.appendChild(chatDrawer);

  function setChatOpen(open: boolean): void {
    chatDrawer.classList.toggle("chat-drawer-open", open);
    chatBackdrop.classList.toggle("chat-backdrop-visible", open);
  }
  closeDrawerBtn.addEventListener("click", () => setChatOpen(false));
  chatBackdrop.addEventListener("click", () => setChatOpen(false));

  // Top bar with forfeit + mobile chat toggle
  const topBar = el("div", { className: "battle-top-bar" });
  topBar.appendChild(button("Chat", () => setChatOpen(true), { variant: "secondary", className: "chat-toggle" }));
  topBar.appendChild(button("Forfeit", () => {
    sync.setEnded();
    clearBattleSession();
    void sync.sendChat("Forfeited");
    navigate("home");
  }, { variant: "danger" }));
  root.appendChild(topBar);

  // Keep mobile drawer log in sync when a chat message arrives
  function addChatMessageToBoth(sender: string, text: string): void {
    addChatMessage(chatLog, sender, text);
    addChatMessage(drawerLog, sender, text);
  }

  container.appendChild(root);

  // Disconnect overlay
  const disconnectOverlay = el("div", {
    className: "battle-disconnect-overlay",
    text: "Opponent disconnected. Waiting for reconnect...",
  });
  disconnectOverlay.style.display = "none";
  disconnectOverlay.style.position = "absolute";
  disconnectOverlay.style.inset = "0";
  disconnectOverlay.style.background = "rgba(0,0,0,0.7)";
  disconnectOverlay.style.color = "var(--text)";
  disconnectOverlay.style.display = "flex";
  disconnectOverlay.style.flexDirection = "column";
  disconnectOverlay.style.alignItems = "center";
  disconnectOverlay.style.justifyContent = "center";
  disconnectOverlay.style.zIndex = "100";
  disconnectOverlay.style.gap = "1rem";
  root.appendChild(disconnectOverlay);

  let timerInterval: ReturnType<typeof setInterval> | null = null;
  let battleEnded = false;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  sync.startHeartbeat();

  /** Update the battle display from a BattleState. */
  function updateBattleState(state: BattleState): void {
    // The engine already masks the opponent bench when getStateForPlayer is used.
    const visibleState = state;

    // Update turn
    clearChildren(turnInfo);
    turnInfo.appendChild(el("span", { text: `Turn ${visibleState.turn}` }));
    if (visibleState.weather) {
      turnInfo.appendChild(el("span", { text: `Weather: ${visibleState.weather}`, className: "weather-info" }));
    }

    // Update sprites and HP
    const foeActive = getActivePokemon(visibleState, foeSide);
    const myActive = getActivePokemon(visibleState, mySide);

    if (foeActive) {
      updateSprite(foeSprite, foeActive.species, foeActive.shiny, false, prefersReducedMotion);
      updateHpBar(foeHpBar, foeActive);
    }
    if (myActive) {
      updateSprite(mySprite, myActive.species, myActive.shiny, true, prefersReducedMotion);
      updateHpBar(myHpBar, myActive);
    }

    if (visibleState.ended || engine.isEnded()) {
      endBattle(state);
      return;
    }

    renderActions(visibleState);
  }

  function renderActions(state: BattleState): void {
    clearChildren(actionPanel);
    const myActive = getActivePokemon(state, mySide);
    if (!myActive || myActive.hp <= 0) {
      actionPanel.appendChild(el("span", { text: "Select a Pokemon to switch in" }));
      renderSwitchButtons(state);
      return;
    }

    // Move buttons
    const moveGrid = el("div", { className: "battle-move-grid" });
    if (myActive.moves.length > 0) {
      for (const move of myActive.moves) {
        moveGrid.appendChild(button(move, () => {
          // Single-target moves: just submit. Multi-target moves: prompt for target.
          const isMultiTarget = isMultiTargetMove(move);
          if (isMultiTarget) {
            promptMoveTarget(state, move);
          } else {
            submitAction({ type: "move", move });
          }
        }, { variant: "secondary" }));
      }
    } else {
      moveGrid.appendChild(button("Struggle", () => {
        submitAction({ type: "struggle" });
      }));
    }
    actionPanel.appendChild(moveGrid);

    // Switch button
    renderSwitchButtons(state);
  }

  function renderSwitchButtons(state: BattleState): void {
    const myTeam = mySide === "p1" ? state.p1 : state.p2;
    for (let i = 0; i < myTeam.length; i++) {
      const mon = myTeam[i];
      if (mon.active || mon.hp <= 0) continue;
      actionPanel.appendChild(createSwitchButton(mon, i, () => {
        submitAction({ type: "switch", slot: i });
      }));
    }
  }

  function createSwitchButton(mon: PokemonBattleState, _slot: number, onClick: () => void): HTMLElement {
    const btn = el("button", {
      className: "btn btn-secondary switch-btn",
      onClick,
    });
    const menuUrl = pokemonMenuUrl(mon.species);
    if (menuUrl) {
      const img = el("img", { className: "switch-menu-sprite", attrs: { alt: mon.nickname || mon.species } }) as HTMLImageElement;
      img.style.imageRendering = "pixelated";
      void resolveSpriteUrl(menuUrl).then((resolved) => {
        if (resolved) img.src = resolved;
      });
      btn.appendChild(img);
    }
    btn.appendChild(el("span", { text: `Switch to ${mon.nickname || mon.species} (${mon.hp}/${mon.maxHp} HP)` }));
    return btn;
  }

  function isMultiTargetMove(move: string): boolean {
    // In Gen 5 singles, only the active opponent is the target. Multi-target moves are
    // those that can hit multiple Pokemon, which in our singles-with-doubles-engine setup
    // means the opponent's active slot (target 0). We expose target selection when the
    // move is known to be a spread move (e.g., Earthquake, Surf, Explosion) or when there
    // is more than one foe active. For simplicity, we use a known list of spread moves.
    const spreadMoves = new Set([
      "Earthquake", "Surf", "Muddy Water", "Explosion", "Self-Destruct", "Lava Plume",
      "Heat Wave", "Searing Shot", "Blizzard", "Discharge", "Boomburst", "Hyper Voice",
      "Eruption", "Water Spout", "Bulldoze", "Rock Slide", "Dazzling Gleam",
    ]);
    return spreadMoves.has(move);
  }

  function promptMoveTarget(state: BattleState, move: string): void {
    clearChildren(actionPanel);
    actionPanel.appendChild(el("p", { text: `Select target for ${move}:` }));
    const foeTeam = mySide === "p1" ? state.p2 : state.p1;
    // For singles, only target index 0 is valid. We still render the active foe.
    for (let i = 0; i < foeTeam.length; i++) {
      const foe = foeTeam[i];
      if (foe.hp <= 0) continue;
      actionPanel.appendChild(button(`${foe.nickname || foe.species} (slot ${i + 1})`, () => {
        submitAction({ type: "move", move, target: i });
      }, { variant: "secondary" }));
    }
    actionPanel.appendChild(button("Cancel", () => {
      updateBattleState(engine.getStateForPlayer(mySide));
    }, { variant: "secondary" }));
  }

  async function submitAction(action: BattleAction): Promise<void> {
    clearChildren(actionPanel);
    actionPanel.appendChild(el("span", { text: "Waiting for opponent..." }));
    const turn = sync.getState().turn;
    await sync.submitAction(turn, action);
  }

  function setSpritesPaused(paused: boolean): void {
    foeSprite.classList.toggle("sprite-paused", paused);
    mySprite.classList.toggle("sprite-paused", paused);
  }

  function resolveTurn(): void {
    if (battleEnded) return;
    setSpritesPaused(true);
    const prevState = engine.getStateForPlayer(mySide);
    const state = session.resolveTurn();
    if (state) {
      const newState = engine.getStateForPlayer(mySide);
      playTurnSounds(prevState, newState, session.getLastActions());
      updateBattleState(newState);
      // Broadcast state to spectators
      void sync.sendBattleState(newState, { p1: mySide === "p1" ? myProfile : opponentProfile, p2: mySide === "p2" ? myProfile : opponentProfile });
      if (!state.ended) {
        startTimer(config.turnTimerSeconds);
      }
    }
    setSpritesPaused(false);
  }

  function playTurnSounds(prev: BattleState, next: BattleState, actions?: { p1: BattleAction; p2: BattleAction } | null): void {
    if (!actions) return;
    // Play move sounds for each side's action
    for (const side of ["p1", "p2"] as PlayerId[]) {
      const action = side === "p1" ? actions.p1 : actions.p2;
      if (action.type === "move") {
        void audioManager.playEffect({ type: "move", move: action.move });
      } else if (action.type === "switch") {
        void audioManager.playEffect({ type: "switch" });
      }
    }
    // Play faint sound if any active Pokemon fainted
    const prevActive = [getActivePokemon(prev, "p1"), getActivePokemon(prev, "p2")];
    const nextActive = [getActivePokemon(next, "p1"), getActivePokemon(next, "p2")];
    for (let i = 0; i < 2; i++) {
      if (prevActive[i] && nextActive[i] && prevActive[i]!.hp > 0 && nextActive[i]!.hp <= 0) {
        void audioManager.playEffect({ type: "faint" });
      }
    }
    // Play hit sound if any active Pokemon took damage
    for (let i = 0; i < 2; i++) {
      if (prevActive[i] && nextActive[i] && nextActive[i]!.hp < prevActive[i]!.hp) {
        void audioManager.playEffect({ type: "hit" });
      }
    }
  }

  function endBattle(state: BattleState): void {
    battleEnded = true;
    stopTimer();
    sync.setEnded();
    audioManager.stopMusic();
    clearChildren(actionPanel);
    const winner = state.winner ?? engine.getWinner();
    const won = winner === mySide;
    void audioManager.playEffect({ type: "battle", sound: won ? "victory" : "defeat" });
    actionPanel.appendChild(el("div", {
      className: `battle-result ${won ? "win" : "lose"}`,
      text: won ? "You Win!" : "You Lose!",
    }));
    actionPanel.appendChild(button("Rematch", async () => {
      const rematchSeed = generateSeed();
      await sync.requestRematch(config, rematchSeed);
      clearChildren(actionPanel);
      actionPanel.appendChild(el("span", { text: "Waiting for opponent to accept rematch..." }));
    }));
    actionPanel.appendChild(button("Back to Home", () => {
      clearBattleSession();
      navigate("home");
    }, { variant: "secondary" }));

    // Auto-save replay to IndexedDB
    void saveReplay(recorder.export(), myProfile.name, opponentProfile.name, state.turn);
  }

  function startTimer(seconds: number): void {
    stopTimer();
    if (seconds <= 0) return;
    let remaining = seconds;
    const timerEl = el("span", { className: "battle-timer" });
    turnInfo.appendChild(timerEl);
    timerEl.textContent = `⏱ ${remaining}s`;
    timerInterval = setInterval(() => {
      remaining--;
      timerEl.textContent = `⏱ ${remaining}s`;
      if (remaining <= 0) {
        stopTimer();
        toast("Time's up!", "warning");
        // Auto-submit a legal action so the battle doesn't stall
        autoSubmitAction();
      }
    }, 1000);
  }

  async function autoSubmitAction(): Promise<void> {
    if (sync.getState().pendingAction !== null) return; // already submitted
    const state = engine.getStateForPlayer(mySide);
    const myActive = getActivePokemon(state, mySide);
    if (!myActive || myActive.hp <= 0) {
      // Must switch — pick first available bench Pokemon
      const myTeam = mySide === "p1" ? state.p1 : state.p2;
      const benchIdx = myTeam.findIndex((p) => !p.active && p.hp > 0);
      if (benchIdx >= 0) {
        await submitAction({ type: "switch", slot: benchIdx });
      }
      return;
    }
    // Try to use a move; fall back to switch if no moves available
    const legal = engine.getLegalActions(mySide).filter((a) => a.type === "move");
    if (legal.length > 0) {
      await submitAction(legal[0]);
    } else {
      const switchActions = engine.getLegalActions(mySide).filter((a) => a.type === "switch");
      if (switchActions.length > 0) {
        await submitAction(switchActions[0]);
      } else {
        await submitAction({ type: "struggle" });
      }
    }
  }

  function stopTimer(): void {
    if (timerInterval) { clearInterval(timerInterval); timerInterval = null; }
  }

  function addChatMessage(log: HTMLElement, sender: string, text: string): void {
    log.appendChild(el("div", { className: "chat-message", text: `${sender}: ${text}` }));
    log.scrollTop = log.scrollHeight;
  }

  function startRematch(newConfig: BattleConfig, seed: number): void {
    battleEnded = false;
    clearBattleSession();
    const newSession = resetBattleSession(session, seed, newConfig);
    // Re-render the battle screen with the new session
    clearChildren(container);
    renderBattle(container, navigate, newSession);
  }

  // Wire sync events to the battle UI
  sync.setTurnReadyCallback(() => {
    resolveTurn();
  });
  sync.setChatCallback((text) => {
    void audioManager.playEffect({ type: "ui", sound: "chat" });
    addChatMessageToBoth(opponentProfile.name, text);
  });
  sync.setPeerDisconnectCallback(() => {
    stopTimer();
    disconnectOverlay.style.display = "flex";
    reconnectTimer = setTimeout(() => {
      toast("Opponent did not reconnect. Returning home.", "error");
      clearBattleSession();
      navigate("home");
    }, 60000);
  });
  sync.setPeerReconnectCallback(() => {
    if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
    disconnectOverlay.style.display = "none";
    toast("Opponent reconnected!", "success");
    // Resume timer if battle still active
    if (!battleEnded) {
      startTimer(config.turnTimerSeconds);
    }
  });
  // Play battle start sound on initial entry
  void audioManager.playEffect({ type: "battle", sound: "start" });
  void audioManager.playMusic("battle");

  sync.setRematchCallbacks(
    (newConfig, seed) => {
      // Rematch requested by opponent: show accept/decline modal
      clearChildren(actionPanel);
      actionPanel.appendChild(el("p", { text: `${opponentProfile.name} wants a rematch!` }));
      actionPanel.appendChild(button("Accept", () => {
        void sync.acceptRematch(newConfig, seed);
        startRematch(newConfig, seed);
      }));
      actionPanel.appendChild(button("Decline", () => {
        void sync.declineRematch();
        clearBattleSession();
        navigate("home");
      }, { variant: "secondary" }));
    },
    (newConfig, seed) => {
      // Rematch accepted by opponent: start the rematch
      startRematch(newConfig, seed);
    },
    () => {
      // Rematch declined by opponent
      toast("Opponent declined rematch", "info");
      clearBattleSession();
      navigate("home");
    },
  );

  // Initial render
  updateBattleState(engine.getStateForPlayer(mySide));
  startTimer(config.turnTimerSeconds);

  // Preload all sprites for this battle when the screen starts
  function preloadBattleSprites(): void {
    const urls: string[] = [];
    const state = engine.getState();
    for (const mon of [...state.p1, ...state.p2]) {
      urls.push(pokemonFrontUrl(mon.species, mon.shiny));
      urls.push(pokemonBackUrl(mon.species, mon.shiny));
    }
    urls.push(backgroundImageUrl(config.battleBackground));
    void preloadSprites(urls);
  }
  preloadBattleSprites();
}

function getActivePokemon(state: BattleState, side: PlayerId): PokemonBattleState | null {
  const team = side === "p1" ? state.p1 : state.p2;
  const activeIdx = side === "p1" ? state.activeP1 : state.activeP2;
  return team[activeIdx] ?? team.find((p) => p.active) ?? null;
}

function createHpBar(): { container: HTMLElement } {
  const container = el("div", { className: "hp-bar-container" });
  const label = el("span", { className: "hp-bar-label", text: "" });
  const barBg = el("div", { className: "hp-bar-bg" });
  const bar = el("div", { className: "hp-bar-fill" });
  barBg.appendChild(bar);
  container.appendChild(label);
  container.appendChild(barBg);
  return { container };
}

function updateHpBar(hpBar: { container: HTMLElement }, pokemon: PokemonBattleState): void {
  const label = hpBar.container.querySelector(".hp-bar-label") as HTMLElement;
  const fill = hpBar.container.querySelector(".hp-bar-fill") as HTMLElement;
  if (label) label.textContent = `${pokemon.nickname || pokemon.species} Lv${pokemon.level}`;
  if (fill) {
    const pct = Math.max(0, (pokemon.hp / pokemon.maxHp) * 100);
    fill.style.width = `${pct}%`;
    fill.className = `hp-bar-fill ${pct > 50 ? "hp-green" : pct > 25 ? "hp-yellow" : "hp-red"}`;
  }
}

function updateSprite(img: HTMLImageElement, species: string, shiny: boolean | undefined, isBack: boolean, reducedMotion = false): void {
  const url = isBack ? pokemonBackUrl(species, shiny) : pokemonFrontUrl(species, shiny);
  if (url) {
    void resolveSpriteUrl(url).then((resolved) => {
      if (resolved) {
        img.src = resolved;
        img.classList.toggle("sprite-animated", !reducedMotion);
      }
    });
  }
}

async function saveReplay(replay: unknown, p1Name: string, p2Name: string, turns: number): Promise<void> {
  try {
    const { dbPut } = await import("../../data/db");
    const { DB_STORES } = await import("../../config/constants");
    await dbPut(DB_STORES.REPLAYS, replay, (replay as any).createdAt);
    toast(`Replay saved (${p1Name} vs ${p2Name}, ${turns} turns)`, "success");
  } catch {
    toast("Failed to save replay", "warning");
  }
}
